/**
 * sl-h128.2.1: Consolidated Workspace AI Settings Tab
 *
 * Single tab combining all Intelligence Suite configuration:
 * 1. Credits + Plans
 * 2. Dokumenten-Analyse (Embedding Settings)
 * 3. AI Memory (Personal Memory + Knowledge Graph)
 * 4. Expert Agents (Bot Management)
 * 5. Usage Analytics
 * 6. Advanced (Provider + Model Configuration)
 */

import { Skeleton, Switch } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import {
  FEATURE_KEY_AI_AUTO_ROUTING,
  FEATURE_KEY_ASSISTANT_TELEGRAM,
  FEATURE_KEY_MANAGED_AI_CREDITS,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { EmbeddingSettings } from '@affine/core/modules/workspace-indexer-embedding';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import type { SettingState } from '../../types';
import { WorkspaceSettingAIMemory } from '../ai-memory';
import { WorkspaceSettingBots } from '../bots';
import { AssistantPolicySettings } from './assistant-policy-settings';
import { BudgetLimitsToggleRow } from './budget-limits-toggle-row';
import { CreditBalanceWidget } from './credit-balance-widget';
import { PdfOcrPolicySettings } from './pdf-ocr-policy-settings';
import { TierModelPanel } from './tier-model-panel';
import { UsageAnalytics } from './usage-analytics';
import { useWorkspaceAISettings } from './use-workspace-ai-settings';

const sectionDescriptionStyle = {
  marginBottom: 10,
  fontSize: 12,
  lineHeight: '20px',
  color: 'var(--affine-text-secondary-color)',
} as const;

const sectionDividerStyle = {
  borderTop: '1px solid var(--streamlain-border-color)',
  margin: '24px 0 8px',
} as const;

export const WorkspaceSettingAI = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspacePermissionService = useService(
    WorkspacePermissionService
  ).permission;
  const isOwner = useLiveData(workspacePermissionService.isOwner$);
  const isAdmin = useLiveData(workspacePermissionService.isAdmin$);

  const canEdit = Boolean(isOwner || isAdmin);
  const settings = useWorkspaceAISettings();
  const { isFeatureAllowed, isLoading: isFeatureGateLoading } =
    useFeatureGate();
  const canUseManagedAiCredits =
    !isFeatureGateLoading && isFeatureAllowed(FEATURE_KEY_MANAGED_AI_CREDITS);
  const canUseAssistantTelegram =
    !isFeatureGateLoading && isFeatureAllowed(FEATURE_KEY_ASSISTANT_TELEGRAM);
  const canUseAutoRouting =
    !isFeatureGateLoading && isFeatureAllowed(FEATURE_KEY_AI_AUTO_ROUTING);

  // Shared toggle state: AI Memory passes changes up, Expert Agents reads them
  const [memoryToggles, setMemoryToggles] = useState({
    personalMemory: false,
    knowledgeGraph: false,
  });
  const [costOverviewPeriodTitle, setCostOverviewPeriodTitle] =
    useState<string>('');

  const title =
    t['com.streamlain.settings.workspace.ai.title']?.() ?? 'AI Settings';
  const subtitle =
    t['com.streamlain.settings.workspace.ai.subtitle']?.() ??
    'AI models, credit balance, and usage analytics for this workspace.';

  const handleNavigateToPlans = useCallback(() => {
    onChangeSettingState?.({
      activeTab: 'workspace:plans',
    });
  }, [onChangeSettingState]);

  const header = (
    <SettingHeader
      title={title}
      subtitle={subtitle}
      action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
    />
  );

  if (settings.isLoading) {
    return (
      <>
        {header}
        {backendAvailable ? (
          <Skeleton height={200} />
        ) : (
          <div style={sectionDescriptionStyle}>{offlineLabel}</div>
        )}
      </>
    );
  }

  return (
    <>
      {header}

      {/* sl-m82f.3.1: highlight AI sub-bar inside the main bar */}
      <CreditBalanceWidget
        onNavigateToPlans={handleNavigateToPlans}
        highlightContext="ai"
      />

      <SettingWrapper
        title={
          t['com.streamlain.settings.workspace.ai.models.section-title']?.() ??
          'AI Models'
        }
      >
        <SettingRow
          name=""
          desc={
            t[
              'com.streamlain.settings.workspace.ai.models.override-desc'
            ]?.() ??
            'Pick workspace-wide default models per tier. Members fall back to their personal preferences or platform defaults for tiers without a workspace override.'
          }
          style={{ marginBottom: 10 }}
        />
        <div style={{ marginBottom: 16 }}>
          <TierModelPanel isAdmin={canEdit} />
        </div>
      </SettingWrapper>

      <WorkspaceSettingAIMemory
        onToggleChange={setMemoryToggles}
        embeddingSettings={
          <EmbeddingSettings
            bare
            advancedExtra={
              canUseManagedAiCredits ? (
                <PdfOcrPolicySettings
                  config={settings.pdfOcrPolicyConfig}
                  canEdit={canEdit}
                  isSaving={settings.isSaving}
                  onChange={settings.setPdfOcrPolicyConfig}
                />
              ) : null
            }
          />
        }
        botsContent={
          <WorkspaceSettingBots
            personalMemoryEnabled={memoryToggles.personalMemory}
            knowledgeGraphEnabled={memoryToggles.knowledgeGraph}
            bare
          />
        }
      />

      <AssistantPolicySettings
        canEdit={canEdit}
        showTelegram={canUseAssistantTelegram}
      />

      <SettingWrapper
        title={t['com.streamlain.settings.workspace.ai.cost-control']()}
      >
        {/* sl-sf9o: Intelligent Model Routing — hidden outside Beta/Internal */}
        {canUseAutoRouting && (
          <SettingRow
            name={t['com.streamlain.settings.workspace.ai.routing.auto']()}
            desc={t['com.streamlain.settings.workspace.ai.routing.auto.desc']()}
          >
            <Switch
              checked={settings.routingConfig.routingMode === 'auto'}
              onChange={(checked: boolean) => {
                void settings.setRoutingConfig({
                  routingMode: checked ? 'auto' : 'off',
                });
              }}
              disabled={!canEdit || settings.isSaving}
            />
          </SettingRow>
        )}

        <BudgetLimitsToggleRow isAdmin={canEdit} />
      </SettingWrapper>

      <SettingWrapper
        title={
          costOverviewPeriodTitle
            ? `${t['com.streamlain.settings.workspace.ai.costs-overview']()} · ${costOverviewPeriodTitle.split(' (')[0]}`
            : t['com.streamlain.settings.workspace.ai.costs-overview']()
        }
      >
        <div style={sectionDividerStyle} />
        <div style={sectionDescriptionStyle}>
          {t['com.streamlain.settings.workspace.ai.costs-overview-desc']()}
        </div>
        <UsageAnalytics
          bare
          excludeTabs={['workflows']}
          onPeriodLabelChange={setCostOverviewPeriodTitle}
        />
      </SettingWrapper>
    </>
  );
};
